// Pin Jobs tab component - manage Pinterest pins for reposting
import { authHeaders } from "../config";

import { useState, useCallback, useEffect } from "react";
import styles from "./PinJobsTab.module.css";
import { apiUrl } from "../config";

interface PinJob {
  id: number;
  original_pin_id: string;
  original_title: string | null;
  original_description: string | null;
  original_image_url: string;
  original_image_url_large: string | null;
  original_pinner_username: string | null;
  original_pinner_name: string | null;
  original_board_name: string | null;
  original_link: string | null;
  original_domain: string | null;
  is_video: number;
  video_url: string | null;
  tags: string;
  rewritten_title: string | null;
  rewritten_description: string | null;
  custom_link: string | null;
  site_id: string | null;
  metricool_account_id: number | null;
  target_board: string | null;
  schedule_date: string | null;
  status: string;
  pinterest_scheduled: number;
  pinterest_scheduled_at: string | null;
  error_message: string | null;
  created_at: string;
}

interface Site {
  id: string;
  name: string;
  domain: string;
  pinterest_brand_name: string;
  pinterest_boards: string;
}

interface CustomLink {
  name: string;
  url: string;
}

interface MetricoolAccount {
  id: number;
  name: string;
  pinterest_brand_name: string;
  pinterest_boards: string[];
  custom_link: string;
  custom_links: CustomLink[];
  is_default: number;
}

interface PinJobsTabProps {
  disabled?: boolean;
}

export function PinJobsTab({ disabled }: PinJobsTabProps) {
  const [pinJobs, setPinJobs] = useState<PinJob[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [metricoolAccounts, setMetricoolAccounts] = useState<MetricoolAccount[]>([]);
  const [selectedJobs, setSelectedJobs] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingJob, setEditingJob] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<{
    rewritten_title: string;
    rewritten_description: string;
    custom_link: string;
    site_id: string;
    metricool_account_id: string;
    target_board: string;
    schedule_date: string;
  }>({
    rewritten_title: "",
    rewritten_description: "",
    custom_link: "",
    site_id: "",
    metricool_account_id: "",
    target_board: "",
    schedule_date: "",
  });
  const [bulkAction, setBulkAction] = useState<string>("");
  const [bulkSiteId, setBulkSiteId] = useState<string>("");
  const [bulkMetricoolAccountId, setBulkMetricoolAccountId] = useState<string>("");
  const [bulkCustomLink, setBulkCustomLink] = useState<string>("");
  const [bulkScheduleDate, setBulkScheduleDate] = useState<string>("");
  const [pinsPerDay, setPinsPerDay] = useState<number>(5);
  const [useDateRange, setUseDateRange] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [scheduleProgress, setScheduleProgress] = useState<{ current: number; total: number; finishDate?: string } | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>("pending");

  // Load pin jobs and sites
  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [jobsRes, sitesRes, metricoolRes] = await Promise.all([
        fetch(apiUrl(`/api/pin-jobs${statusFilter !== "all" ? `?status=${statusFilter}` : ""}`), { headers: authHeaders() }),
        fetch(apiUrl("/api/sites"), { headers: authHeaders() }),
        fetch(apiUrl("/api/metricool/accounts"), { headers: authHeaders() }),
      ]);

      if (jobsRes.ok) {
        const jobsText = await jobsRes.text();
        if (jobsText) {
          const jobsData = JSON.parse(jobsText);
          setPinJobs(jobsData.pinJobs || []);
        }
      }

      if (sitesRes.ok) {
        const sitesText = await sitesRes.text();
        if (sitesText) {
          const sitesData = JSON.parse(sitesText);
          setSites(sitesData.sites || []);
        }
      }

      if (metricoolRes.ok) {
        const metricoolText = await metricoolRes.text();
        if (metricoolText) {
          const metricoolData = JSON.parse(metricoolText);
          setMetricoolAccounts(metricoolData.accounts || []);
        }
      }
    } catch (err) {
      console.error("Failed to load data:", err);
      setError("Failed to load data");
    } finally {
      setIsLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleToggleSelect = (id: number) => {
    setSelectedJobs((prev) =>
      prev.includes(id) ? prev.filter((j) => j !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedJobs.length === pinJobs.length) {
      setSelectedJobs([]);
    } else {
      setSelectedJobs(pinJobs.map((j) => j.id));
    }
  };

  const handleSelectCount = (count: number) => {
    if (count <= 0) {
      setSelectedJobs([]);
    } else {
      setSelectedJobs(pinJobs.slice(0, count).map((j) => j.id));
    }
  };

  const handleEdit = (job: PinJob) => {
    setEditingJob(job.id);
    setEditForm({
      rewritten_title: job.rewritten_title || job.original_title || "",
      rewritten_description: job.rewritten_description || job.original_description || "",
      custom_link: job.custom_link || "",
      site_id: job.site_id || "",
      metricool_account_id: job.metricool_account_id ? String(job.metricool_account_id) : "",
      target_board: job.target_board || "",
      schedule_date: job.schedule_date || "",
    });
  };

  const handleSaveEdit = async () => {
    if (!editingJob) return;

    try {
      // Build payload - only send site_id OR metricool_account_id
      const payload: Record<string, unknown> = {
        rewritten_title: editForm.rewritten_title,
        rewritten_description: editForm.rewritten_description,
        custom_link: editForm.custom_link,
        target_board: editForm.target_board,
        schedule_date: editForm.schedule_date,
      };

      if (editForm.metricool_account_id) {
        payload.metricool_account_id = parseInt(editForm.metricool_account_id);
        payload.site_id = null;
      } else if (editForm.site_id) {
        payload.site_id = editForm.site_id;
        payload.metricool_account_id = null;
      }

      const response = await fetch(apiUrl(`/api/pin-jobs/${editingJob}`), {
        method: "PATCH",
        headers: authHeaders(),
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        await loadData();
        setEditingJob(null);
      } else {
        const data = await response.json();
        setError(data.error || "Failed to save");
      }
    } catch (err) {
      setError("Failed to save changes");
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm("Delete this pin job?")) return;

    try {
      const response = await fetch(apiUrl(`/api/pin-jobs/${id}`), { method: "DELETE", headers: authHeaders() });
      if (response.ok) {
        await loadData();
        setSelectedJobs((prev) => prev.filter((j) => j !== id));
      }
    } catch (err) {
      setError("Failed to delete");
    }
  };

  const handleBulkDelete = async () => {
    if (!confirm(`Delete ${selectedJobs.length} pin jobs?`)) return;

    setIsProcessing(true);
    try {
      await Promise.all(
        selectedJobs.map((id) =>
          fetch(apiUrl(`/api/pin-jobs/${id}`), { method: "DELETE", headers: authHeaders() })
        )
      );
      await loadData();
      setSelectedJobs([]);
    } catch (err) {
      setError("Failed to delete some jobs");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRewrite = async (id: number) => {
    setIsProcessing(true);
    try {
      const response = await fetch(apiUrl(`/api/pin-jobs/${id}/rewrite`), {
        method: "POST",
        headers: authHeaders(),
      });
      const data = await response.json();
      if (data.success) {
        await loadData();
      } else {
        setError(data.error || "Failed to rewrite");
      }
    } catch (err) {
      setError("Failed to rewrite");
    } finally {
      setIsProcessing(false);
    }
  };

  const [rewriteProgress, setRewriteProgress] = useState<{ current: number; total: number } | null>(null);

  const handleBulkRewrite = async () => {
    if (selectedJobs.length === 0) return;

    // Only rewrite jobs that haven't been rewritten yet
    const jobsToRewrite = selectedJobs.filter((id) => {
      const job = pinJobs.find((j) => j.id === id);
      return job && !job.rewritten_title;
    });

    if (jobsToRewrite.length === 0) return;

    setIsProcessing(true);
    setError(null);
    setRewriteProgress({ current: 0, total: jobsToRewrite.length });

    let successCount = 0;
    const BATCH_SIZE = 10;

    // Process in batches of 10 parallel requests
    for (let batchStart = 0; batchStart < jobsToRewrite.length; batchStart += BATCH_SIZE) {
      const batch = jobsToRewrite.slice(batchStart, batchStart + BATCH_SIZE);

      // Wait 5 seconds between batches (except for first batch)
      if (batchStart > 0) {
        await new Promise(resolve => setTimeout(resolve, 5000));
      }

      // Process batch in parallel
      const results = await Promise.allSettled(
        batch.map(async (jobId) => {
          const response = await fetch(apiUrl(`/api/pin-jobs/${jobId}/rewrite`), {
            method: "POST",
            headers: authHeaders(),
          });
          const data = await response.json();
          return data.success;
        })
      );

      // Count successes
      results.forEach((result) => {
        if (result.status === "fulfilled" && result.value) {
          successCount++;
        }
      });

      setRewriteProgress({ current: Math.min(batchStart + BATCH_SIZE, jobsToRewrite.length), total: jobsToRewrite.length });
    }

    await loadData();
    setRewriteProgress(null);

    if (successCount < jobsToRewrite.length) {
      setError(`Rewrote ${successCount}/${jobsToRewrite.length} jobs`);
    } else {
      setError(`✅ Rewrote ${successCount} jobs successfully!`);
    }

    setIsProcessing(false);
  };

  // Count how many selected jobs need rewriting
  const selectedNeedRewrite = selectedJobs.filter((id) => {
    const job = pinJobs.find((j) => j.id === id);
    return job && !job.rewritten_title;
  }).length;

  const handleSchedule = async (id: number) => {
    setIsProcessing(true);
    try {
      const response = await fetch(apiUrl(`/api/pin-jobs/${id}/schedule`), {
        method: "POST",
        headers: authHeaders(),
      });
      const data = await response.json();
      if (data.success) {
        await loadData();
      } else {
        setError(data.error || "Failed to schedule");
      }
    } catch (err) {
      setError("Failed to schedule");
    } finally {
      setIsProcessing(false);
    }
  };

  // Helper to generate dates with X pins per day starting from start date
  const getDatesByPinsPerDay = (startDate: string, pinsPerDay: number, count: number): string[] => {
    const start = new Date(startDate);
    const dates: string[] = [];

    for (let i = 0; i < count; i++) {
      const dayOffset = Math.floor(i / pinsPerDay);
      const date = new Date(start);
      date.setDate(date.getDate() + dayOffset);
      dates.push(date.toISOString().split('T')[0] as string);
    }
    return dates;
  };

  // Calculate finish date based on pins per day
  const calculateFinishDate = (startDate: string, pinsPerDay: number, count: number): string => {
    const totalDays = Math.ceil(count / pinsPerDay) - 1;
    const start = new Date(startDate);
    start.setDate(start.getDate() + totalDays);
    return start.toISOString().split('T')[0] as string;
  };

  const handleBulkSchedule = async () => {
    if (selectedJobs.length === 0) return;

    setIsProcessing(true);
    setError(null);
    setScheduleProgress(null);

    try {
      // Build schedule data - include dates if provided
      let scheduleDates: Record<number, string> | undefined;
      let finishDate: string | undefined;

      if (useDateRange && bulkScheduleDate && pinsPerDay > 0) {
        // Distribute pins: X pins per day starting from start date
        const distributedDates = getDatesByPinsPerDay(bulkScheduleDate, pinsPerDay, selectedJobs.length);
        scheduleDates = {};
        selectedJobs.forEach((id, index) => {
          scheduleDates![id] = distributedDates[index] ?? bulkScheduleDate;
        });
        finishDate = calculateFinishDate(bulkScheduleDate, pinsPerDay, selectedJobs.length);
        setScheduleProgress({ current: 0, total: selectedJobs.length, finishDate });
      } else if (bulkScheduleDate) {
        // Single date for all
        scheduleDates = {};
        selectedJobs.forEach((id) => {
          scheduleDates![id] = bulkScheduleDate;
        });
        finishDate = bulkScheduleDate;
      }

      // Process one by one with delays to avoid Metricool rate limits
      // 1s between posts, 5s when switching to a new day
      let successCount = 0;
      const results: Array<{ id: number; success: boolean; error?: string }> = [];
      let lastDate: string | null = null;

      for (let i = 0; i < selectedJobs.length; i++) {
        const jobId = selectedJobs[i]!;
        const currentDate = scheduleDates?.[jobId];
        setScheduleProgress({ current: i + 1, total: selectedJobs.length, finishDate });

        // Wait between requests (except for first one)
        if (i > 0) {
          // 5s delay when switching to a new day, 1s between posts on same day
          const delay = (currentDate && lastDate && currentDate !== lastDate) ? 5000 : 1000;
          await new Promise(resolve => setTimeout(resolve, delay));
        }
        lastDate = currentDate || null;

        try {
          // First update the schedule date
          if (scheduleDates && scheduleDates[jobId]) {
            await fetch(apiUrl(`/api/pin-jobs/${jobId}`), {
              method: "PATCH",
              headers: authHeaders(),
              body: JSON.stringify({ schedule_date: scheduleDates[jobId] }),
            });
          }

          // Then schedule to Metricool
          const response = await fetch(apiUrl(`/api/pin-jobs/${jobId}/schedule`), {
            method: "POST",
            headers: authHeaders(),
          });
          const data = await response.json();

          if (data.success) {
            successCount++;
            results.push({ id: jobId, success: true });
          } else {
            results.push({ id: jobId, success: false, error: data.error });
          }
        } catch (err) {
          results.push({ id: jobId, success: false, error: "Request failed" });
        }
      }

      await loadData();

      if (successCount < selectedJobs.length) {
        setError(`Scheduled ${successCount}/${selectedJobs.length} jobs. Finish date: ${finishDate || "N/A"}`);
      } else {
        setError(`✅ Scheduled ${successCount} jobs! Finish date: ${finishDate || "N/A"}`);
      }

      setSelectedJobs([]);
      setBulkScheduleDate("");
      setUseDateRange(false);
      setScheduleProgress(null);
    } catch (err) {
      setError("Failed to schedule jobs");
      setScheduleProgress(null);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBulkUpdateSite = async () => {
    if (selectedJobs.length === 0 || !bulkSiteId) return;

    setIsProcessing(true);
    try {
      await Promise.all(
        selectedJobs.map((id) =>
          fetch(apiUrl(`/api/pin-jobs/${id}`), {
            method: "PATCH",
            headers: authHeaders(),
            body: JSON.stringify({ site_id: bulkSiteId, metricool_account_id: null }),
          })
        )
      );
      await loadData();
    } catch (err) {
      setError("Failed to update site");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBulkUpdateMetricoolAccount = async () => {
    if (selectedJobs.length === 0 || !bulkMetricoolAccountId) return;

    setIsProcessing(true);
    try {
      await Promise.all(
        selectedJobs.map((id) =>
          fetch(apiUrl(`/api/pin-jobs/${id}`), {
            method: "PATCH",
            headers: authHeaders(),
            body: JSON.stringify({ metricool_account_id: parseInt(bulkMetricoolAccountId), site_id: null }),
          })
        )
      );
      await loadData();
    } catch (err) {
      setError("Failed to update metricool account");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBulkUpdateLink = async () => {
    if (selectedJobs.length === 0 || !bulkCustomLink) return;

    setIsProcessing(true);
    try {
      // Check if random mode is selected
      if (bulkCustomLink === "__random__") {
        const selectedAccount = metricoolAccounts.find(
          (a) => a.id === parseInt(bulkMetricoolAccountId)
        );
        const customLinks = selectedAccount?.custom_links || [];

        if (customLinks.length === 0) {
          setError("No custom links available for random assignment");
          setIsProcessing(false);
          return;
        }

        // Assign random links to each selected job
        await Promise.all(
          selectedJobs.map((id) => {
            const randomLink = customLinks[Math.floor(Math.random() * customLinks.length)]!;
            return fetch(apiUrl(`/api/pin-jobs/${id}`), {
              method: "PATCH",
              headers: authHeaders(),
              body: JSON.stringify({ custom_link: randomLink.url }),
            });
          })
        );
      } else {
        // Normal mode - apply same link to all
        await Promise.all(
          selectedJobs.map((id) =>
            fetch(apiUrl(`/api/pin-jobs/${id}`), {
              method: "PATCH",
              headers: authHeaders(),
              body: JSON.stringify({ custom_link: bulkCustomLink }),
            })
          )
        );
      }
      await loadData();
      setBulkCustomLink("");
    } catch (err) {
      setError("Failed to update link");
    } finally {
      setIsProcessing(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "scheduled":
        return styles.statusScheduled;
      case "error":
        return styles.statusError;
      case "pending":
      default:
        return styles.statusPending;
    }
  };

  const getSiteBoards = (siteId: string): string[] => {
    const site = sites.find((s) => s.id === siteId);
    if (!site) return [];
    try {
      return JSON.parse(site.pinterest_boards || "[]");
    } catch {
      return [];
    }
  };

  const getMetricoolAccountBoards = (accountId: string): string[] => {
    const account = metricoolAccounts.find((a) => a.id === parseInt(accountId));
    return account?.pinterest_boards || [];
  };

  const getActiveBoards = (): string[] => {
    if (editForm.metricool_account_id) {
      return getMetricoolAccountBoards(editForm.metricool_account_id);
    }
    if (editForm.site_id) {
      return getSiteBoards(editForm.site_id);
    }
    return [];
  };

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <h2 className={styles.title}>Pin Jobs</h2>
        <div className={styles.headerActions}>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="scheduled">Scheduled</option>
            <option value="error">Error</option>
          </select>
          <button onClick={loadData} className={styles.refreshBtn} disabled={isLoading}>
            {isLoading ? "Loading..." : "Refresh"}
          </button>
        </div>
      </div>

      {error && (
        <div className={styles.error}>
          {error}
          <button onClick={() => setError(null)} className={styles.dismissBtn}>×</button>
        </div>
      )}

      {/* Bulk Actions */}
      {selectedJobs.length > 0 && (
        <div className={styles.bulkActions}>
          <div className={styles.bulkActionsHeader}>
            <span className={styles.selectedCount}>
              {selectedJobs.length} pin{selectedJobs.length > 1 ? 's' : ''} selected
            </span>
            <button
              onClick={handleBulkDelete}
              className={styles.bulkBtnDanger}
              disabled={isProcessing}
            >
              Delete Selected
            </button>
          </div>
          <div className={styles.bulkButtons}>
            {/* Account Group */}
            <div className={styles.bulkGroup}>
              <span className={styles.bulkGroupLabel}>Account</span>
              <select
                value={bulkMetricoolAccountId}
                onChange={(e) => {
                  const accountId = e.target.value;
                  setBulkMetricoolAccountId(accountId);
                  setBulkCustomLink("");
                }}
                className={styles.bulkSelect}
              >
                <option value="">Select Account...</option>
                {metricoolAccounts.map((acc) => (
                  <option key={acc.id} value={acc.id}>
                    {acc.name}
                  </option>
                ))}
              </select>
              <button
                onClick={handleBulkUpdateMetricoolAccount}
                className={styles.bulkBtn}
                disabled={isProcessing || !bulkMetricoolAccountId}
              >
                Apply
              </button>
            </div>

            {/* Link Group */}
            <div className={styles.bulkGroup}>
              <span className={styles.bulkGroupLabel}>Link</span>
              {(() => {
                const selectedAccount = metricoolAccounts.find(
                  (a) => a.id === parseInt(bulkMetricoolAccountId)
                );
                const hasCustomLinks = selectedAccount?.custom_links && selectedAccount.custom_links.length > 0;

                if (hasCustomLinks) {
                  return (
                    <select
                      value={bulkCustomLink}
                      onChange={(e) => setBulkCustomLink(e.target.value)}
                      className={styles.bulkSelect}
                    >
                      <option value="">Select Link...</option>
                      <option value="__random__">🎲 Random</option>
                      {selectedAccount.custom_links.map((link) => (
                        <option key={link.name} value={link.url}>
                          {link.name}
                        </option>
                      ))}
                    </select>
                  );
                }
                return (
                  <input
                    type="text"
                    value={bulkCustomLink}
                    onChange={(e) => setBulkCustomLink(e.target.value)}
                    placeholder="Enter link URL..."
                    className={styles.bulkInput}
                  />
                );
              })()}
              <button
                onClick={handleBulkUpdateLink}
                className={styles.bulkBtn}
                disabled={isProcessing}
              >
                Apply
              </button>
            </div>

            {/* Rewrite */}
            <button
              onClick={handleBulkRewrite}
              className={styles.bulkBtn}
              disabled={isProcessing || selectedNeedRewrite === 0}
              title={selectedNeedRewrite === 0 ? "All selected pins already rewritten" : `Rewrite ${selectedNeedRewrite} pins`}
            >
              {rewriteProgress
                ? `Rewriting ${rewriteProgress.current}/${rewriteProgress.total}...`
                : `Rewrite${selectedNeedRewrite > 0 ? ` (${selectedNeedRewrite})` : ""}`}
            </button>

            {/* Schedule Group */}
            <div className={styles.bulkGroup}>
              <span className={styles.bulkGroupLabel}>Schedule</span>
              <label className={styles.dateRangeToggle} title="Split posts across multiple days">
                <input
                  type="checkbox"
                  checked={useDateRange}
                  onChange={(e) => setUseDateRange(e.target.checked)}
                />
                Multi-day
              </label>
              <input
                type="date"
                value={bulkScheduleDate}
                onChange={(e) => setBulkScheduleDate(e.target.value)}
                className={styles.bulkInput}
                title={useDateRange ? "Start date" : "Schedule date (leave empty for next day)"}
              />
              {useDateRange && (
                <>
                  <input
                    type="number"
                    value={pinsPerDay}
                    onChange={(e) => setPinsPerDay(Math.max(1, parseInt(e.target.value) || 1))}
                    className={styles.bulkInput}
                    title="Pins per day"
                    min="1"
                    style={{ width: "70px" }}
                  />
                  <span className={styles.bulkGroupLabel}>per day</span>
                </>
              )}
              {scheduleProgress && (
                <span className={styles.progressInfo}>
                  {scheduleProgress.current}/{scheduleProgress.total}
                </span>
              )}
              <button
                onClick={handleBulkSchedule}
                className={styles.bulkBtnPrimary}
                disabled={isProcessing || (useDateRange && !bulkScheduleDate)}
                title={useDateRange && bulkScheduleDate ? `Schedule ${selectedJobs.length} posts (${pinsPerDay}/day)` : "Schedule all selected"}
              >
                {isProcessing ? `Scheduling...` : "Schedule All"}
              </button>
            </div>
            {useDateRange && bulkScheduleDate && (
              <span className={styles.finishDateInfo}>
                Finish: {calculateFinishDate(bulkScheduleDate, pinsPerDay, selectedJobs.length)}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Pin Jobs List */}
      {isLoading && pinJobs.length === 0 ? (
        <div className={styles.loading}>Loading...</div>
      ) : pinJobs.length === 0 ? (
        <div className={styles.empty}>
          No pin jobs yet. Search Pinterest and click "Add to Pin Jobs" to get started.
        </div>
      ) : (
        <div className={styles.jobsList}>
          <div className={styles.listHeader}>
            <label className={styles.selectAllLabel}>
              <input
                type="checkbox"
                checked={selectedJobs.length === pinJobs.length && pinJobs.length > 0}
                onChange={handleSelectAll}
              />
              Select All
            </label>
            <div className={styles.selectCountWrapper}>
              <input
                type="number"
                min="0"
                max={pinJobs.length}
                placeholder="Select #"
                className={styles.selectCountInput}
                onChange={(e) => handleSelectCount(parseInt(e.target.value) || 0)}
              />
            </div>
          </div>

          {pinJobs.map((job) => (
            <div
              key={job.id}
              className={`${styles.jobCard} ${selectedJobs.includes(job.id) ? styles.selected : ""}`}
            >
              <div className={styles.jobCheckbox}>
                <input
                  type="checkbox"
                  checked={selectedJobs.includes(job.id)}
                  onChange={() => handleToggleSelect(job.id)}
                />
              </div>

              <div className={styles.jobImage}>
                <img
                  src={job.original_image_url}
                  alt={job.original_title || "Pin"}
                  loading="lazy"
                />
                {job.is_video === 1 && <span className={styles.videoBadge}>Video</span>}
              </div>

              <div className={styles.jobContent}>
                {editingJob === job.id ? (
                  <div className={styles.editForm}>
                    <input
                      type="text"
                      value={editForm.rewritten_title}
                      onChange={(e) =>
                        setEditForm({ ...editForm, rewritten_title: e.target.value })
                      }
                      placeholder="Rewritten title"
                      className={styles.editInput}
                    />
                    <textarea
                      value={editForm.rewritten_description}
                      onChange={(e) =>
                        setEditForm({ ...editForm, rewritten_description: e.target.value })
                      }
                      placeholder="Rewritten description"
                      className={styles.editTextarea}
                      rows={3}
                    />
                    {/* Custom Link - show dropdown if account has custom_links, else text input */}
                    {(() => {
                      const selectedAccount = metricoolAccounts.find(
                        (a) => a.id === parseInt(editForm.metricool_account_id)
                      );
                      const hasCustomLinks = selectedAccount?.custom_links && selectedAccount.custom_links.length > 0;

                      if (hasCustomLinks) {
                        return (
                          <select
                            value={editForm.custom_link}
                            onChange={(e) => setEditForm({ ...editForm, custom_link: e.target.value })}
                            className={styles.editSelect}
                          >
                            <option value="">Select Link...</option>
                            {selectedAccount.custom_links.map((link) => (
                              <option key={link.name} value={link.url}>
                                {link.name}
                              </option>
                            ))}
                          </select>
                        );
                      }
                      return (
                        <input
                          type="text"
                          value={editForm.custom_link}
                          onChange={(e) => setEditForm({ ...editForm, custom_link: e.target.value })}
                          placeholder="Custom link (optional)"
                          className={styles.editInput}
                        />
                      );
                    })()}
                    <div className={styles.editRow}>
                      <select
                        value={editForm.site_id}
                        onChange={(e) =>
                          setEditForm({ ...editForm, site_id: e.target.value, metricool_account_id: "", target_board: "" })
                        }
                        className={styles.editSelect}
                      >
                        <option value="">Select Site...</option>
                        {sites.map((site) => (
                          <option key={site.id} value={site.id}>
                            {site.name}
                          </option>
                        ))}
                      </select>
                      <span className={styles.orText}>or</span>
                      <select
                        value={editForm.metricool_account_id}
                        onChange={(e) => {
                          const accountId = e.target.value;
                          setEditForm({
                            ...editForm,
                            metricool_account_id: accountId,
                            site_id: "",
                            target_board: "",
                            custom_link: "", // Clear link when account changes
                          });
                        }}
                        className={styles.editSelect}
                      >
                        <option value="">Metricool Account...</option>
                        {metricoolAccounts.map((acc) => (
                          <option key={acc.id} value={acc.id}>
                            {acc.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className={styles.editRow}>
                      <select
                        value={editForm.target_board}
                        onChange={(e) =>
                          setEditForm({ ...editForm, target_board: e.target.value })
                        }
                        className={styles.editSelect}
                        disabled={!editForm.site_id && !editForm.metricool_account_id}
                      >
                        <option value="">Auto-detect board</option>
                        {getActiveBoards().map((board) => (
                          <option key={board} value={board}>
                            {board}
                          </option>
                        ))}
                      </select>
                    </div>
                    <input
                      type="date"
                      value={editForm.schedule_date}
                      onChange={(e) =>
                        setEditForm({ ...editForm, schedule_date: e.target.value })
                      }
                      className={styles.editInput}
                    />
                    <div className={styles.editActions}>
                      <button onClick={handleSaveEdit} className={styles.saveBtn}>
                        Save
                      </button>
                      <button
                        onClick={() => setEditingJob(null)}
                        className={styles.cancelBtn}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className={styles.jobTitle}>
                      {job.rewritten_title || job.original_title || "Untitled"}
                      {job.rewritten_title && (
                        <span className={styles.rewrittenBadge}>Rewritten</span>
                      )}
                    </div>
                    <div className={styles.jobDescription}>
                      {(job.rewritten_description || job.original_description || "").slice(0, 150)}
                      {(job.rewritten_description || job.original_description || "").length > 150 ? "..." : ""}
                    </div>
                    <div className={styles.jobMeta}>
                      {job.custom_link && (
                        <span className={styles.metaItem}>
                          🔗 {job.custom_link.slice(0, 40)}...
                        </span>
                      )}
                      {job.site_id && (
                        <span className={styles.metaItem}>
                          📍 {sites.find((s) => s.id === job.site_id)?.name || job.site_id}
                        </span>
                      )}
                      {job.metricool_account_id && (
                        <span className={styles.metaItem}>
                          📊 {metricoolAccounts.find((a) => a.id === job.metricool_account_id)?.name || `Account #${job.metricool_account_id}`}
                        </span>
                      )}
                      {job.target_board && (
                        <span className={styles.metaItem}>📌 {job.target_board}</span>
                      )}
                    </div>
                    <div className={styles.jobFooter}>
                      <span className={`${styles.status} ${getStatusColor(job.status)}`}>
                        {job.status}
                      </span>
                      {job.pinterest_scheduled_at && (
                        <span className={styles.scheduledAt}>
                          Scheduled: {new Date(job.pinterest_scheduled_at).toLocaleString()}
                        </span>
                      )}
                      {job.error_message && (
                        <span className={styles.errorMsg}>{job.error_message}</span>
                      )}
                    </div>
                  </>
                )}
              </div>

              <div className={styles.jobActions}>
                <button
                  onClick={() => handleEdit(job)}
                  className={styles.actionBtn}
                  disabled={editingJob === job.id}
                  title="Edit"
                >
                  ✏️
                </button>
                <button
                  onClick={() => handleRewrite(job.id)}
                  className={styles.actionBtn}
                  disabled={isProcessing}
                  title="AI Rewrite"
                >
                  🤖
                </button>
                <button
                  onClick={() => handleSchedule(job.id)}
                  className={styles.actionBtn}
                  disabled={isProcessing || !job.rewritten_title || !job.custom_link || (!job.site_id && !job.metricool_account_id)}
                  title="Schedule"
                >
                  📤
                </button>
                <button
                  onClick={() => handleDelete(job.id)}
                  className={styles.actionBtnDanger}
                  title="Delete"
                >
                  🗑️
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
